import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Logo from "../components/Logo";
import {
  TOTAL_STEPS,
  STEP_LABELS,
  REQUIRED_KEY_BY_STEP,
  SPACE_OPTIONS,
  CHRONOTYPE_OPTIONS,
  ENTERTAIN_OPTIONS,
  GUEST_GATHER_OPTIONS,
  WORK_NEEDS_PEOPLE,
  WORK_NEEDS_OPTIONS,
  UNWIND_OPTIONS,
  PASSION_OPTIONS,
  EXPERIENCE_OPTIONS,
  STYLE_DEFS,
  COLOR_DEFS,
  PREF_DEFS,
  LABEL_MAP,
} from "./surveyData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COMPANY = "Folk Objects";

/* ---------- Small primitive components ---------- */

function Chip({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`folk-chip ${selected ? "selected" : ""}`}
    >
      {children}
    </button>
  );
}

function ListBtn({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`folk-btn-list ${selected ? "selected" : ""}`}
    >
      {children}
    </button>
  );
}

function BigBtn({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`folk-btn ${selected ? "selected" : ""}`}
      style={{ flex: 1 }}
    >
      {children}
    </button>
  );
}

function ImgSlot({ src, alt }) {
  if (!src) {
    return <div className="folk-img-ph">Photo</div>;
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    />
  );
}

function SectionEyebrow({ children }) {
  return <div className="folk-section">{children}</div>;
}

/* ---------- Main survey ---------- */

export default function Survey() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get("edit");
  const isEdit = !!editId;

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [fiveWordDraft, setFiveWordDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(isEdit ? editId : null);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [editUnauthorized, setEditUnauthorized] = useState(false);

  // On mount in edit mode: fetch existing answers and prefill.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/survey/responses/${editId}/answers`, { withCredentials: true });
        if (cancelled) return;
        const raw = data.answers_raw || {};
        setAnswers({
          ...raw,
          contactName: data.contact_name || raw.contactName || "",
          contactEmail: data.contact_email || raw.contactEmail || "",
        });
        setStep(1); // Skip the welcome/banner step in edit mode
      } catch (e) {
        if (e.response?.status === 401 || e.response?.status === 403) {
          setEditUnauthorized(true);
        } else {
          toast.error("Couldn't load your answers. Please try again.");
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editId]);

  const setSingle = useCallback((key, value) => {
    setAnswers((s) => ({ ...s, [key]: value }));
  }, []);

  const setText = useCallback(
    (key) => (e) => setAnswers((s) => ({ ...s, [key]: e.target.value })),
    []
  );

  const toggleMulti = useCallback((key, value, max) => {
    setAnswers((s) => {
      const cur = s[key] || [];
      const has = cur.includes(value);
      let next;
      if (has) next = cur.filter((v) => v !== value);
      else {
        if (max && cur.length >= max) return s;
        next = [...cur, value];
      }
      return { ...s, [key]: next };
    });
  }, []);

  const isFilled = useCallback(
    (s) => {
      // Step 27 (basics) requires both name and email
      if (s === 27) {
        const name = (answers.contactName || "").trim();
        const email = (answers.contactEmail || "").trim();
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return !!name && emailOk;
      }
      const key = REQUIRED_KEY_BY_STEP[s];
      if (!key) return true;
      const v = answers[key];
      if (Array.isArray(v)) return v.length > 0;
      return !!(v && String(v).trim());
    },
    [answers]
  );

  const submitToBackend = useCallback(async (finalAnswers) => {
    setSubmitting(true);
    try {
      const flat = {};
      Object.keys(LABEL_MAP).forEach((k) => {
        const v = finalAnswers[k];
        if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return;
        flat[LABEL_MAP[k]] = Array.isArray(v) ? v.join(", ") : v;
      });
      const referralCode = new URLSearchParams(location.search).get("ref");
      const payload = {
        answers: flat,
        answers_raw: finalAnswers,
        contact_name: finalAnswers.contactName || null,
        contact_email: finalAnswers.contactEmail || null,
        referral_code: referralCode || null,
      };
      if (isEdit && editId) {
        await axios.put(`${API}/survey/responses/${editId}/answers`, payload, { withCredentials: true });
        toast.success("Your answers have been updated.");
        setSubmissionId(editId);
      } else {
        const r = await axios.post(`${API}/survey/submit`, payload);
        if (r?.data?.id) setSubmissionId(r.data.id);
      }
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "We couldn't save your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [isEdit, editId]);

  const next = useCallback(() => {
    setStep((s) => {
      const nextStep = Math.min(s + 1, TOTAL_STEPS - 1);
      if (nextStep === TOTAL_STEPS - 1 && s !== TOTAL_STEPS - 1) {
        submitToBackend(answers);
      }
      return nextStep;
    });
  }, [answers, submitToBackend]);

  const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);
  const restart = useCallback(() => {
    setStep(0);
    setAnswers({});
    setFiveWordDraft("");
    setSubmitted(false);
    setSubmissionId(null);
  }, []);

  const isFirst = step === 0;
  const isLast = step === TOTAL_STEPS - 1;
  const showChrome = !isFirst && !isLast;
  const canContinue = isFilled(step);
  const progressPct = Math.round((step / (TOTAL_STEPS - 2)) * 100);

  /* ---------- Step renderers ---------- */

  const renderStep = () => {
    const a = answers;
    switch (step) {
      case 0:
        return (
          <div className="folk-fade-in survey-intro" style={{ marginTop: "2vh" }}>
            <div className="survey-intro__image">
              <ImgSlot src="/refs/welcome.webp" alt="Your home starts here" />
            </div>
            <div className="survey-intro__body">
              <div style={{ fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>
                Homeowner Survey · Folk Objects
              </div>
              <h1 className="folk-h1" style={{ maxWidth: 520 }}>Let's design your everyday <em style={{ fontStyle: "italic" }}>life</em>.</h1>
              <p className="folk-body" style={{ maxWidth: 480 }}>
                A few honest questions about how you live, not just how you decorate. Takes about 10–15 focused minutes, and shapes a space you'll live in for years.
              </p>
              <button data-testid="begin-btn" type="button" onClick={next} className="folk-pill folk-pill-lg">Begin</button>
            </div>
          </div>
        );

      case 1:
        return (
          <StepShell title="Who lives in the home?" section="Household basics" help="Adults, children and ages if you're comfortable sharing, plus any pets.">
            <textarea
              data-testid="household-input"
              rows={4}
              className="folk-textarea"
              placeholder="e.g. Two adults, twin boys age 6, one very opinionated cat"
              value={a.household || ""}
              onChange={setText("household")}
            />
          </StepShell>
        );

      case 2: {
        const showDetail = a.hasChildren === "Yes";
        const count = a.childCount || 1;
        return (
          <StepShell title="Do you have children at home?" section="Household basics" help="This helps us plan for how the space needs to flex as they grow.">
            <div style={{ display: "flex", gap: 12, marginBottom: 22 }}>
              {["Yes", "No"].map((v) => (
                <BigBtn
                  key={v}
                  selected={a.hasChildren === v}
                  testId={`children-${v.toLowerCase()}`}
                  onClick={() => {
                    setAnswers((s) => ({
                      ...s,
                      hasChildren: v,
                      childCount: v === "Yes" && !s.childCount ? 1 : s.childCount,
                    }));
                  }}
                >
                  {v}
                </BigBtn>
              ))}
            </div>
            {showDetail && (
              <div>
                <p className="folk-tiny" style={{ marginBottom: 10 }}>How many?</p>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
                  <button
                    type="button"
                    data-testid="child-dec"
                    onClick={() => setAnswers((s) => ({ ...s, childCount: Math.max((s.childCount || 1) - 1, 1) }))}
                    style={{ width: 40, height: 40, borderRadius: 999, border: "1.5px solid rgba(27,26,26,0.18)", background: "var(--folk-card)", fontSize: 18, cursor: "pointer" }}
                  >
                    −
                  </button>
                  <div data-testid="child-count" style={{ fontSize: 20, fontWeight: 600, minWidth: 20, textAlign: "center" }}>{count}</div>
                  <button
                    type="button"
                    data-testid="child-inc"
                    onClick={() => setAnswers((s) => ({ ...s, childCount: Math.min((s.childCount || 1) + 1, 8) }))}
                    style={{ width: 40, height: 40, borderRadius: 999, border: "1.5px solid rgba(27,26,26,0.18)", background: "var(--folk-card)", fontSize: 18, cursor: "pointer" }}
                  >
                    +
                  </button>
                </div>
                <p className="folk-tiny" style={{ marginBottom: 10 }}>
                  Tell us about each one: age, and how they use the living space (homework at the kitchen table, sprawled on the living room floor, needs a playroom, etc.)
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {Array.from({ length: count }, (_, i) => (
                    <input
                      key={i}
                      data-testid={`child-note-${i}`}
                      className="folk-input"
                      type="text"
                      placeholder={`Child ${i + 1}: age & how they use the space`}
                      value={(a.childrenNotes && a.childrenNotes[i]) || ""}
                      onChange={(e) =>
                        setAnswers((s) => {
                          const notes = (s.childrenNotes || []).slice();
                          notes[i] = e.target.value;
                          return { ...s, childrenNotes: notes };
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </StepShell>
        );
      }

      case 3:
        return (
          <StepShell title="Any accessibility or physical needs we should design around?" section="Household basics" help="For anyone in the household: mobility, sensory, or anything else that matters day to day.">
            <textarea
              data-testid="accessibility-input"
              rows={4}
              className="folk-textarea"
              placeholder="e.g. My mother-in-law uses a walker and visits often"
              value={a.accessibility || ""}
              onChange={setText("accessibility")}
            />
          </StepShell>
        );

      case 4: {
        const showOther = (a.spaces || []).includes("Other");
        return (
          <StepShell title="Which spaces are the priority for this project?" section="Household basics">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {SPACE_OPTIONS.map((opt) => (
                <Chip
                  key={opt}
                  selected={(a.spaces || []).includes(opt)}
                  onClick={() => toggleMulti("spaces", opt)}
                  testId={`space-${opt.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  {opt}
                </Chip>
              ))}
            </div>
            {showOther && (
              <input
                data-testid="spaces-other"
                className="folk-input"
                style={{ marginTop: 14 }}
                type="text"
                placeholder="Tell us more"
                value={a.spacesOtherText || ""}
                onChange={setText("spacesOtherText")}
              />
            )}
          </StepShell>
        );
      }

      case 5: {
        const showDetail = a.staffYesNo === "Yes";
        return (
          <StepShell title="Do you have any full-time staff?" section="Household basics">
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              {["Yes", "No"].map((v) => (
                <BigBtn key={v} selected={a.staffYesNo === v} testId={`staff-${v.toLowerCase()}`} onClick={() => setSingle("staffYesNo", v)}>
                  {v}
                </BigBtn>
              ))}
            </div>
            {showDetail && (
              <textarea
                data-testid="staff-detail"
                rows={3}
                className="folk-textarea"
                placeholder="Who, and what areas might they need (staff quarters, service entry, etc.)"
                value={a.staffDetail || ""}
                onChange={setText("staffDetail")}
              />
            )}
          </StepShell>
        );
      }

      case 6:
        return (
          <StepShell title="Walk us through a typical morning." section="Lifestyle & routines" help="Weekdays and weekends: where does everyone start the day, and what does the flow feel like?">
            <p className="folk-tiny">Weekday mornings</p>
            <textarea
              data-testid="weekday-input"
              rows={3}
              className="folk-textarea"
              placeholder="e.g. Everyone gets ready at once, one bathroom becomes chaos..."
              value={a.weekday || ""}
              onChange={setText("weekday")}
            />
            <p className="folk-tiny" style={{ marginTop: 20 }}>Weekend mornings</p>
            <textarea
              data-testid="saturday-input"
              rows={3}
              className="folk-textarea"
              placeholder="e.g. Kids are on the living room rug with cartoons, we're making coffee in the kitchen..."
              value={a.saturday || ""}
              onChange={setText("saturday")}
            />
          </StepShell>
        );

      case 7:
        return (
          <StepShell title="Morning people or night owls?" section="Lifestyle & routines">
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              {CHRONOTYPE_OPTIONS.map((v) => (
                <ListBtn key={v} selected={a.chronotype === v} onClick={() => setSingle("chronotype", v)} testId={`chrono-${v.replace(/\W+/g, "-").toLowerCase()}`}>
                  {v}
                </ListBtn>
              ))}
            </div>
            <p className="folk-tiny">How does natural light affect your routine?</p>
            <input
              data-testid="light-notes"
              className="folk-input"
              type="text"
              placeholder="e.g. We need blackout curtains, or the opposite, more sun"
              value={a.lightNotes || ""}
              onChange={setText("lightNotes")}
            />
          </StepShell>
        );

      case 8:
        return (
          <StepShell title="Do you like to entertain?" section="Entertaining">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {ENTERTAIN_OPTIONS.map((v) => (
                <ListBtn key={v} selected={a.entertainStyle === v} onClick={() => setSingle("entertainStyle", v)} testId={`entertain-${v.replace(/\W+/g, "-").toLowerCase()}`}>
                  {v}
                </ListBtn>
              ))}
            </div>
            <p className="folk-tiny">How often, roughly?</p>
            <input data-testid="entertain-frequency" className="folk-input" placeholder="e.g. Once a month, mostly weekends" value={a.entertainFrequency || ""} onChange={setText("entertainFrequency")} />
            <p className="folk-tiny" style={{ marginTop: 20 }}>Do you like to cook, host, or explore fun passions together: cooking, coffee, drinks, and the like?</p>
            <input data-testid="cook-passions" className="folk-input" placeholder="e.g. We love hosting wine tastings; my partner's really into specialty coffee" value={a.cookPassions || ""} onChange={setText("cookPassions")} />
          </StepShell>
        );

      case 9: {
        const showOther = (a.guestGather || []).includes("Somewhere else");
        return (
          <StepShell title="When guests come over, where does everyone naturally gather?" section="Entertaining">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {GUEST_GATHER_OPTIONS.map((v) => (
                <Chip
                  key={v}
                  selected={(a.guestGather || []).includes(v)}
                  onClick={() => toggleMulti("guestGather", v)}
                  testId={`gather-${v.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  {v}
                </Chip>
              ))}
            </div>
            {showOther && (
              <input data-testid="gather-other" className="folk-input" style={{ marginTop: 14 }} placeholder="Where's that?" value={a.guestGatherOtherText || ""} onChange={setText("guestGatherOtherText")} />
            )}
          </StepShell>
        );
      }

      case 10:
        return (
          <StepShell title="Does anyone work or study from home?" section="Work, rest & play">
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {WORK_NEEDS_PEOPLE.map((p) => (
                <div key={p.key}>
                  <div style={{ fontSize: 13, color: "var(--folk-hint)", marginBottom: 8 }}>{p.who}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {WORK_NEEDS_OPTIONS.map((opt) => (
                      <ListBtn key={opt} selected={a[p.key] === opt} onClick={() => setSingle(p.key, opt)} testId={`${p.key}-${opt.split(" ")[0].toLowerCase()}`}>
                        {opt}
                      </ListBtn>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </StepShell>
        );

      case 11: {
        const showOther = (a.unwind || []).includes("Other");
        return (
          <StepShell title="How do you and the family unwind?" section="Work, rest & play">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {UNWIND_OPTIONS.map((v) => (
                <Chip key={v} selected={(a.unwind || []).includes(v)} onClick={() => toggleMulti("unwind", v)} testId={`unwind-${v.replace(/\W+/g, "-").toLowerCase()}`}>
                  {v}
                </Chip>
              ))}
            </div>
            {showOther && (
              <input data-testid="unwind-other" className="folk-input" style={{ marginTop: 14 }} placeholder="Tell us more" value={a.unwindOtherText || ""} onChange={setText("unwindOtherText")} />
            )}
          </StepShell>
        );
      }

      case 12: {
        const showDetail = a.workoutYesNo === "Yes";
        return (
          <StepShell title="Does anybody work out or exercise at home?" section="Work, rest & play">
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              {["Yes", "No"].map((v) => (
                <BigBtn key={v} selected={a.workoutYesNo === v} testId={`workout-${v.toLowerCase()}`} onClick={() => setSingle("workoutYesNo", v)}>{v}</BigBtn>
              ))}
            </div>
            {showDetail && (
              <input data-testid="workout-detail" className="folk-input" placeholder="e.g. Yoga in the mornings, needs mat storage and a mirror" value={a.workoutDetail || ""} onChange={setText("workoutDetail")} />
            )}
          </StepShell>
        );
      }

      case 13:
        return (
          <StepShell title="What isn't working in your current home layout?" section="Work, rest & play">
            <textarea data-testid="not-working" rows={4} className="folk-textarea" placeholder="Be as honest and specific as you like." value={a.notWorking || ""} onChange={setText("notWorking")} />
          </StepShell>
        );

      case 14: {
        const selectedStyles = a.styles || [];
        return (
          <StepShell title="Which of these resonate with you most?" section="Aesthetic & style" help={`Pick up to three. (${selectedStyles.length} of 3 selected)`}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(360px, 100%), 1fr))", gap: 22 }}>
              {STYLE_DEFS.map((d) => {
                const selected = selectedStyles.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleMulti("styles", d.value, 3)}
                    data-testid={`style-${d.value}`}
                    className={`folk-style-card ${selected ? "selected" : ""}`}
                  >
                    <div className={`folk-check ${selected ? "selected" : ""}`}>{selected ? "✓" : ""}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, borderRadius: 12, overflow: "hidden" }}>
                      {[1, 2, 3, 4].map((n) => (
                        <div key={n} style={{ aspectRatio: "1/1", position: "relative", background: "#F3ECE9" }}>
                          {d.hasRefs ? <ImgSlot src={`/refs/style-${d.slot}-${n}.png`} alt={d.label} /> : <div className="folk-img-ph">{d.label}</div>}
                        </div>
                      ))}
                    </div>
                    <div style={{ textAlign: "left", padding: "6px 4px 2px" }}>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>{d.label}</div>
                      <div style={{ fontSize: 14, color: "var(--folk-muted)", marginTop: 4, lineHeight: 1.45 }}>{d.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </StepShell>
        );
      }

      case 15:
        return (
          <StepShell title="What's your relationship with color?" section="Color & texture">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(440px, 100%), 1fr))", gap: 24 }}>
              {COLOR_DEFS.map((d) => {
                const selected = a.colorRelationship === d.value;
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setSingle("colorRelationship", d.value)}
                    data-testid={`color-${d.value}`}
                    className={`folk-color-card ${selected ? "selected" : ""}`}
                  >
                    <div style={{ display: "flex", height: 220, borderRadius: 12, overflow: "hidden", gap: d.imgs ? 8 : 0 }}>
                      {d.imgs ? (
                        d.imgs.map((src, i) => (
                          <div key={i} style={{ flex: 1, overflow: "hidden", borderRadius: 8 }}>
                            <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          </div>
                        ))
                      ) : (
                        d.swatches.map((sw, i) => (
                          <div
                            key={i}
                            style={{
                              flex: 1,
                              background: sw,
                              borderLeft: i === 0 ? "none" : sw === "#FFFFFF" ? "1px solid var(--folk-border)" : "none",
                            }}
                          />
                        ))
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 600, color: selected ? "var(--folk-accent)" : "#2A2828" }}>{d.label}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                          {d.swatches.map((sw, i) => (
                            <div key={i} style={{ width: 20, height: 20, borderRadius: 5, background: sw, border: sw === "#FFFFFF" ? "1px solid var(--folk-border)" : "none" }} />
                          ))}
                        </div>
                      </div>
                      <div style={{ width: 26, height: 26, borderRadius: 999, background: selected ? "var(--folk-accent)" : "rgba(27,26,26,0.35)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, flexShrink: 0 }}>
                        {selected ? "✓" : ""}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </StepShell>
        );

      case 16:
        return (
          <StepShell title="Any colors or patterns you absolutely want to avoid?" section="Color & texture">
            <textarea data-testid="colors-avoid" rows={3} className="folk-textarea" placeholder="e.g. No orange, no busy florals" value={a.colorsToAvoid || ""} onChange={setText("colorsToAvoid")} />
          </StepShell>
        );

      case 17:
        return (
          <StepShell title="A few quick either/ors." section="Look & feel">
            <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
              {PREF_DEFS.map((d) => {
                const val = a[d.key];
                return (
                  <div key={d.key}>
                    <div style={{ fontSize: 14, color: "var(--folk-hint)", marginBottom: 10 }}>{d.question}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      {[
                        { side: "a", label: d.aLabel, img: d.aImg },
                        { side: "b", label: d.bLabel, img: d.bImg },
                      ].map(({ side, label, img }) => (
                        <button
                          key={side}
                          type="button"
                          onClick={() => setSingle(d.key, side)}
                          data-testid={`${d.key}-${side}`}
                          className={`folk-image-btn ${val === side ? "selected" : ""}`}
                        >
                          <div style={{ height: 340, position: "relative", minWidth: 0, background: "#F3ECE9" }}>
                            <ImgSlot src={img} alt={label} />
                          </div>
                          <span style={{ padding: 18, fontSize: 16, textAlign: "left" }}>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </StepShell>
        );

      case 18:
        return (
          <StepShell title="Any existing pieces, art, or heirlooms that must be included?" section="Look & feel">
            <textarea data-testid="heirlooms" rows={4} className="folk-textarea" placeholder="e.g. My grandmother's dining table, a painting from our honeymoon" value={a.heirlooms || ""} onChange={setText("heirlooms")} />
          </StepShell>
        );

      case 19:
        return (
          <StepShell title="Are there places you've traveled that inspire how you want your home to feel?" section="Inspiration & values" help="A color, a material, a mood, a memory, anything that comes to mind. Think Santorini's whitewashed calm, Kyoto's stillness, Marrakech's souks, or a lake house from childhood.">
            <textarea data-testid="travel-inspo" rows={4} className="folk-textarea" placeholder="e.g. The light in the south of France, the calm of a Kyoto ryokan" value={a.travelInspo || ""} onChange={setText("travelInspo")} />
          </StepShell>
        );

      case 20:
        return (
          <StepShell title="What art styles do you love? Any craft traditions you're drawn to?" section="Inspiration & values" help="Weaving, pottery, woodworking, block printing, anything handmade that speaks to you.">
            <textarea data-testid="art-craft" rows={4} className="folk-textarea" placeholder="e.g. Abstract expressionism, hand-thrown ceramics, embroidery" value={a.artCraft || ""} onChange={setText("artCraft")} />
          </StepShell>
        );

      case 21:
        return (
          <StepShell title="Any passions that should shape our choices?" section="Inspiration & values">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {PASSION_OPTIONS.map((v) => (
                <Chip key={v} selected={(a.passions || []).includes(v)} onClick={() => toggleMulti("passions", v)} testId={`passion-${v.replace(/\W+/g, "-").toLowerCase()}`}>
                  {v}
                </Chip>
              ))}
            </div>
          </StepShell>
        );

      case 22:
        return (
          <StepShell title="What's the one thing you wish we, as your design team, understood about you?" section="Inspiration & values">
            <textarea data-testid="one-wish" rows={4} className="folk-textarea" placeholder="Say whatever feels most important." value={a.oneWish || ""} onChange={setText("oneWish")} />
          </StepShell>
        );

      case 23:
        return (
          <StepShell title="A few dates to plan around." section="Timeline">
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <p className="folk-tiny">When is the flat expected to be delivered / handed over to you?</p>
                <input data-testid="delivery-date" className="folk-input" placeholder="e.g. March 2027, or still uncertain" value={a.deliveryDate || ""} onChange={setText("deliveryDate")} />
              </div>
              <div>
                <p className="folk-tiny">When do you expect the fixed interiors (civil work, electrical, plumbing) to be complete?</p>
                <input data-testid="fixed-date" className="folk-input" placeholder="e.g. Two months after handover" value={a.fixedInteriorsDate || ""} onChange={setText("fixedInteriorsDate")} />
              </div>
              <div>
                <p className="folk-tiny">When would you like to move in?</p>
                <input data-testid="movein-date" className="folk-input" placeholder="e.g. By the end of next year" value={a.moveInDate || ""} onChange={setText("moveInDate")} />
              </div>
            </div>
          </StepShell>
        );

      case 24: {
        const showOther = a.experienceType === "Something else";
        return (
          <StepShell title="What kind of experience do you want working with us?" section="Working together">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {EXPERIENCE_OPTIONS.map((v) => (
                <ListBtn key={v} selected={a.experienceType === v} onClick={() => setSingle("experienceType", v)} testId={`experience-${v.split(" ")[0].toLowerCase()}`}>
                  {v}
                </ListBtn>
              ))}
            </div>
            {showOther && (
              <input data-testid="experience-other" className="folk-input" style={{ marginTop: 14 }} placeholder="Tell us what would work best" value={a.experienceOtherText || ""} onChange={setText("experienceOtherText")} />
            )}
          </StepShell>
        );
      }

      case 25: {
        const SUPPORT_OPTIONS = [
          { key: "design_only", label: "Design only. I've got my contractor and project team sorted." },
          { key: "turnkey", label: "Turnkey. Take the whole thing off my plate." },
          { key: "styling_details", label: "I already have an interior designer, I just need help with products, styling and the little details." },
          { key: "rescue", label: "I hate my current team. Just help me." },
          { key: "unsure", label: "I have noooo idea. What should I pick?" },
        ];
        return (
          <StepShell
            title="Alright, let's get to business. What kind of support do you need?"
            section="Working together"
            help="Pick whichever feels closest. We'll dig into the details when we speak."
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {SUPPORT_OPTIONS.map((opt) => (
                <ListBtn
                  key={opt.key}
                  selected={a.supportType === opt.key}
                  onClick={() => setSingle("supportType", opt.key)}
                  testId={`support-${opt.key}`}
                >
                  {opt.label}
                </ListBtn>
              ))}
            </div>
            {a.supportType === "unsure" && (
              <div style={{ marginTop: 16, background: "#BADDF0", border: "1.5px solid rgba(27, 74, 102, 0.2)", borderRadius: 12, padding: "14px 16px", color: "#0F3A54", fontSize: 14, lineHeight: 1.55 }}>
                Totally fine, pick "no idea" and we'll figure out the right level of support together on our first call.
              </div>
            )}
          </StepShell>
        );
      }

      case 26: {
        const words = a.fiveWords || [];
        const canAdd = words.length < 5;
        const addWord = () => {
          const w = (fiveWordDraft || "").trim();
          if (!w) return;
          setAnswers((s) => {
            const list = s.fiveWords || [];
            if (list.length >= 5) return s;
            return { ...s, fiveWords: [...list, w] };
          });
          setFiveWordDraft("");
        };
        return (
          <StepShell
            title="If your finished home could speak, what five words would it say to someone walking in?"
            section="The feeling"
            help="e.g. Welcoming, serene, vibrant, organized, cozy"
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, minHeight: 20 }}>
              {words.map((w, i) => (
                <span key={`${w}-${i}`} className="folk-word-chip" data-testid={`word-chip-${i}`}>
                  {w}
                  <span
                    className="rm"
                    onClick={() =>
                      setAnswers((s) => ({
                        ...s,
                        fiveWords: (s.fiveWords || []).filter((_, idx) => idx !== i),
                      }))
                    }
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
            {canAdd && (
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  data-testid="word-input"
                  className="folk-input"
                  style={{ flex: 1 }}
                  placeholder="Type a word and press enter"
                  value={fiveWordDraft}
                  onChange={(e) => setFiveWordDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addWord();
                    }
                  }}
                />
                <button data-testid="word-add" type="button" onClick={addWord} style={{ border: "none", background: "var(--folk-accent)", color: "var(--folk-card)", borderRadius: 14, padding: "0 22px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif" }}>
                  Add
                </button>
              </div>
            )}
          </StepShell>
        );
      }

      case 27:
        return (
          <StepShell
            title="Where should we send updates?"
            section="Almost done"
            help="Both fields are required so we can send your welcome pack and portal link."
          >
            <p className="folk-tiny">Your name <span style={{ color: "var(--folk-accent)" }}>*</span></p>
            <input
              data-testid="contact-name"
              className="folk-input"
              placeholder="Enter your name"
              value={a.contactName || ""}
              onChange={setText("contactName")}
              required
            />
            <p className="folk-tiny" style={{ marginTop: 20 }}>Email address <span style={{ color: "var(--folk-accent)" }}>*</span></p>
            <input
              data-testid="contact-email"
              className="folk-input"
              type="email"
              placeholder="you@gmail.com"
              value={a.contactEmail || ""}
              onChange={setText("contactEmail")}
              required
            />
          </StepShell>
        );

      case 28:
        return (
          <div className="folk-fade-in" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 22, marginTop: "8vh" }} data-testid="thankyou-screen">
            <div style={{ width: 64, height: 64, borderRadius: 999, background: "var(--folk-accent-2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 30 }}>
              {submitting ? "…" : "✓"}
            </div>
            <h1 className="folk-h1" style={{ maxWidth: 440 }}>
              {isEdit ? "Your updates are in." : "Thank you. We're already picturing it."}
            </h1>
            <p className="folk-body" style={{ maxWidth: 480 }}>
              {isEdit
                ? `We've saved your latest answers. Your Folk team can see the changes and we'll refresh your persona summary shortly.`
                : `Your answers have been sent to your design team at ${COMPANY}. We'll be in touch within two business days to walk through what we heard and set up a first working session.`}
            </p>

            {isEdit && submissionId && (
              <Link
                to={`/portal/${submissionId}`}
                data-testid="back-to-portal-link"
                className="folk-pill"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                Back to my portal →
              </Link>
            )}

            {submissionId && (
              <div
                data-testid="portal-cta"
                style={{
                  background: "var(--folk-card)",
                  border: "1.5px solid var(--folk-border)",
                  borderRadius: 18,
                  padding: 22,
                  maxWidth: 480,
                  width: "100%",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  textAlign: "left",
                }}
              >
                <div style={{ fontSize: 13, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 600 }}>
                  Your private client portal
                </div>
                <div style={{ fontSize: 15, color: "#2A2828", lineHeight: 1.55 }}>
                  Bookmark this link. Your welcome letter, moodboard, design process and install-day details will live here throughout the project.
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    background: "#BADDF0",
                    borderRadius: 12,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    data-testid="portal-url"
                    style={{
                      flex: 1,
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: 13,
                      color: "#2A2828",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`${window.location.origin}/portal/${submissionId}`}
                  </div>
                  <button
                    type="button"
                    data-testid="copy-portal-url"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${window.location.origin}/portal/${submissionId}`);
                        toast.success("Link copied");
                      } catch {
                        toast.error("Couldn't copy, please copy it manually.");
                      }
                    }}
                    style={{
                      border: "1.5px solid var(--folk-accent)",
                      background: "transparent",
                      color: "var(--folk-accent)",
                      padding: "6px 12px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "Host Grotesk, sans-serif",
                    }}
                  >
                    Copy
                  </button>
                </div>
                <a
                  href={`/portal/${submissionId}`}
                  data-testid="open-portal"
                  className="folk-pill"
                  style={{ alignSelf: "flex-start", textDecoration: "none" }}
                >
                  Open my portal
                </a>
              </div>
            )}

            {!submitting && !submitted && (
              <button type="button" className="folk-pill" onClick={() => submitToBackend(answers)} data-testid="retry-submit">
                Retry sending
              </button>
            )}
            <a data-testid="restart-btn" onClick={restart} style={{ cursor: "pointer", fontSize: 14, color: "var(--folk-accent)", textDecoration: "underline", marginTop: 8 }}>
              Start over
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  // Loading + auth gating for edit mode
  if (isEdit && loadingEdit) {
    return (
      <div style={{ minHeight: "100vh", background: "#FFFBC9", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--folk-text)", fontFamily: "'Playfair Display', serif", fontSize: 20 }}>
        Loading your answers…
      </div>
    );
  }
  if (isEdit && editUnauthorized) {
    return (
      <div style={{ minHeight: "100vh", background: "#FFFBC9", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, textAlign: "center", padding: 32, color: "var(--folk-text)" }}>
        <h1 className="folk-h1" style={{ maxWidth: 480 }}>please sign in to edit your survey.</h1>
        <p className="folk-body" style={{ maxWidth: 460 }}>
          For your privacy, only you (or your Folk team) can update these answers. Sign in with the email you used on your original survey.
        </p>
        <Link to="/portal-login" data-testid="edit-signin-link" className="folk-pill" style={{ textDecoration: "none" }}>
          Homeowner sign in →
        </Link>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", width: "100%", background: "#FFFBC9", display: "flex", flexDirection: "column", alignItems: "center", color: "var(--folk-text)", boxSizing: "border-box" }}>
      {isEdit && (
        <div data-testid="edit-mode-banner" style={{ width: "100%", background: "#BADDF0", color: "#0F3A54", padding: "10px 16px", textAlign: "center", fontSize: 13.5, fontWeight: 500 }}>
          You're editing an existing survey.{" "}
          {submissionId && (
            <Link to={`/portal/${submissionId}`} style={{ color: "#0F3A54", fontWeight: 600, textDecoration: "underline" }}>
              Cancel &amp; return to portal
            </Link>
          )}
        </div>
      )}
      {/* Brand header FOLK ... OBJECTS with sign-in tucked under OBJECTS */}
      <div style={{ width: "100%", maxWidth: 1440, padding: "28px 48px 40px", boxSizing: "border-box" }}>
        <Logo variant="split" size={24} testId="folk-brand-header" />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <a
            href="/portal-login"
            data-testid="client-signin-link"
            style={{
              padding: "8px 16px", borderRadius: 999,
              border: "1.5px solid rgba(27, 74, 102, 0.25)",
              background: "#BADDF0", color: "#0F3A54",
              textDecoration: "none", fontFamily: "Host Grotesk, sans-serif",
              fontSize: 13, fontWeight: 600,
            }}
          >
            Homeowner sign in
          </a>
        </div>
      </div>

      {/* Top bar */}
      <div style={{ width: "100%", maxWidth: 1440, padding: "20px 48px 0", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 20, minHeight: 40 }}>
        {showChrome && (
          <>
            <button className="folk-back" onClick={back} data-testid="back-btn">
              <span style={{ fontSize: 18 }}>←</span> Back
            </button>
            <div className="folk-progress">
              <div className="folk-progress-bar" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="folk-step-label" data-testid="step-label">
              {STEP_LABELS[step] || ""}
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div style={{ width: "100%", maxWidth: 1440, flex: 1, display: "flex", flexDirection: "column", padding: "32px 48px 140px", boxSizing: "border-box" }}>
        {renderStep()}
      </div>

      {/* Footer */}
      {showChrome && (
        <div className="folk-footer-fixed" style={{ background: "linear-gradient(to top, #FFFBC9 60%, rgba(255, 251, 201, 0))" }}>
          <div style={{ width: "100%", maxWidth: 1440, display: "flex", justifyContent: "flex-end", padding: "0 48px", boxSizing: "border-box" }}>
            <button
              type="button"
              onClick={next}
              disabled={!canContinue}
              className="folk-pill"
              style={{ opacity: canContinue ? 1 : 0.45 }}
              data-testid="next-btn"
            >
              {step === TOTAL_STEPS - 2 ? "Finish" : "Continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Shared per-step wrapper ---------- */
function StepShell({ section, title, help, children }) {
  return (
    <div className="folk-fade-in">
      {section && <SectionEyebrow>{section}</SectionEyebrow>}
      <h2 className="folk-h2">{title}</h2>
      {help && <p className="folk-help">{help}</p>}
      {children}
    </div>
  );
}
